import { APIComponentAttributes } from '@ardoq/api-types';
import { useEffect, useState } from 'react';
import { AgEventListener, GridApi } from 'ag-grid-enterprise';

import { InventoryContext } from '../types';

/**
 * Custom React hook for AG Grid popup editors that need to close themselves when the grid is horizontally scrolled.
 *
 * This is useful for popup editors (`cellEditorPopup: true`) that may otherwise remain visually detached
 * or incorrectly positioned during horizontal scroll events, particularly in pinned or floating rows.
 *
 * Usage: Call this inside a popup editor component to automatically close it on horizontal scroll.
 */
export const useEffectToHideEditorOnHorizontalScrollForPopupEditors = (
  api: GridApi<APIComponentAttributes>
) => {
  useEffect(() => {
    const handleBodyScroll: AgEventListener<
      APIComponentAttributes,
      InventoryContext,
      'bodyScroll'
    > = event => {
      if (event.direction === 'horizontal') {
        api.stopEditing();
      }
    };

    api.addEventListener('bodyScroll', handleBodyScroll);

    return () => {
      api.removeEventListener('bodyScroll', handleBodyScroll);
    };
  }, [api]);
};

export const useEffectToHideEditorOnHorizontalScrollForReferenceEditors = (
  api: GridApi<APIComponentAttributes>
) => {
  const [isScrollToEnsureColumnIsVisible, setIsScrollToEnsureColumnIsVisible] =
    useState(true);

  useEffect(() => {
    const handleBodyScroll: AgEventListener<
      APIComponentAttributes,
      InventoryContext,
      'bodyScroll'
    > = () => {
      /* Because the reference editor might need to be programmatically scrolled into view if the column isn't completely showing,
       * We have to ignore the first scroll event and cause a rerender to make the ModalBox render in the right position */
      if (isScrollToEnsureColumnIsVisible) {
        setIsScrollToEnsureColumnIsVisible(false);
        return;
      }
      api.stopEditing();
    };

    api.addEventListener('bodyScroll', handleBodyScroll);

    return () => {
      api.removeEventListener('bodyScroll', handleBodyScroll);
    };
  }, [
    api,
    isScrollToEnsureColumnIsVisible,
    setIsScrollToEnsureColumnIsVisible,
  ]);
};
